import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    Typography,
    Button,
    IconButton,
    Collapse,
    Avatar,
    MenuItem,
    FormControl,
    Select,
    InputLabel,
    Chip,
    Badge,
    Link,
    Modal,
    Tooltip,
    Skeleton,
    TextField,
    InputAdornment,
    Pagination,
} from "@mui/material";
import { red, yellow, green } from "@mui/material/colors";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {
    fetchUserFaqs,
    markFaqAsRead,
    acceptFaq,
    updateFavourite,
    checkWelcomeTourStatus,
    markWelcomeTourCompleted,
} from "../../api/api";
import { Snackbar, Alert } from "@mui/material";
import { getUserId, getCookie } from "../../utils/cookieUtils";
import WelcomeTour from "../../components/WelcomeTour/WelcomeTour";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

// Helper function to get category chip color
const getCategoryColor = (category) => {
    const categoryColors = {
        'Generell Info': { bg: '#e3f2fd', color: '#1976d2' },
        'Rutiner & Policy': { bg: '#f3e5f5', color: '#7b1fa2' },
        'Kasse & Betaling': { bg: '#e8f5e9', color: '#388e3c' },
        'Salg & Promotering': { bg: '#fff3e0', color: '#f57c00' },
        'Produkter': { bg: '#fce4ec', color: '#c2185b' },
        'Tjenester': { bg: '#e0f2f1', color: '#00796b' },
        'Personal/Arbeid': { bg: '#fff9c4', color: '#f9a825' },
        'Kunder': { bg: '#e1bee7', color: '#8e24aa' },
    };
    return categoryColors[category] || { bg: '#f5f5f5', color: '#616161' };
};

const Home = () => {
    const [expandedCard, setExpandedCard] = useState(null); // Track expanded card
    const [faqs, setFaqs] = useState([]);
    // "+points" / "module complete" message after reading a post
    const [readFeedback, setReadFeedback] = useState(null);
    const [userId, setUserId] = useState(null);
    const navigate = useNavigate(); // Redirect function
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [previousCard, setPreviousCard] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [loading, setLoading] = useState(true);
    // Pre-filled from ?search= when opening a post from the Training page
    const [searchQuery, setSearchQuery] = useState(
        () => new URLSearchParams(window.location.search).get("search") || ""
    );
    const [showWelcomeTour, setShowWelcomeTour] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [paginationMeta, setPaginationMeta] = useState(null);
    // ✅ Unread/read counts from the backend, independent of the currently
    // selected status filter/tab and pagination — this is what the NEW/READ
    // red dot badge is driven by, so it stays accurate no matter which tab
    // you're viewing.
    const [statusCounts, setStatusCounts] = useState({ unread: 0, read: 0 });
    // const userId = 1; // Replace this with dynamic user ID (e.g., from auth state or localStorage)

    const handleImageClick = (url) => {
        setSelectedImage(url);
        setModalOpen(true);
    };

    const handleClose = () => {
        setModalOpen(false);
        setSelectedImage(null);
    };

    // Function to handle status filtering
    const handleStatusFilter = (status) => {
        setSelectedCategory(null);
        setSelectedStatus((prevStatus) =>
            prevStatus === status ? null : status
        ); // Toggle same status off
    };

    const isMessageStarred = (message) => {
        if (typeof message.tempStarred === "boolean") {
            return message.tempStarred;
        }
        return (
            message.is_starred === true ||
            message.is_starred === 1 ||
            message.is_starred === "1"
        );
    };

    // Function to handle category filtering
    const handleCategoryFilter = (category) => {
        if (category === "") {
            // "OK" click to reset category to "All Categories"
            setSelectedCategory(null);
        } else {
            // Toggle the same category off
            setSelectedCategory((prevCategory) =>
                prevCategory === category ? null : category
            );
        }
    };

    useEffect(() => {
        // Prefer cookie; also accept switch URL params so we never bounce to login mid-bootstrap
        const urlParams = new URLSearchParams(window.location.search);
        const userIdFromUrl = urlParams.get('user_id');
        const storedUserId = getUserId() || userIdFromUrl;

        if (!storedUserId) {
            navigate("/"); // Redirect only if userId is missing
        } else {
            setUserId(storedUserId);
        }
    }, [navigate]);

    // Check welcome tour status on component mount (after user_id cookie is available)
    useEffect(() => {
        let retryCount = 0;
        const maxRetries = 5;
        
        const checkTour = async () => {
            // Wait a bit to ensure cookies are set after redirect
            const storedUserId = getCookie('user_id');
            console.log('Checking welcome tour - user_id cookie:', storedUserId);
            
            if (!storedUserId) {
                retryCount++;
                if (retryCount < maxRetries) {
                    // If no user_id yet, wait a bit and try again
                    console.log(`Retrying welcome tour check (${retryCount}/${maxRetries})...`);
                    setTimeout(() => {
                        checkTour();
                    }, 500);
                } else {
                    console.warn('Max retries reached for welcome tour check');
                }
                return;
            }

            try {
                const result = await checkWelcomeTourStatus();
                console.log('Welcome tour status:', result);
                if (result.status && !result.has_seen_tour) {
                    console.log('Showing welcome tour');
                    setShowWelcomeTour(true);
                } else if (!result.status) {
                    console.warn('Welcome tour check failed:', result.debug || 'Unknown error');
                }
            } catch (error) {
                console.error('Error checking welcome tour:', error);
            }
        };
        
        // Small delay to ensure cookies are set after page load
        setTimeout(() => {
            checkTour();
        }, 500);
    }, []);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedStatus, selectedCategory, searchQuery]);

    // ✅ Define fetchFaqs function using useCallback so it can be called from other places
    const fetchFaqs = useCallback(async () => {
        if (!userId) return;
        
        setLoading(true);
        try {
            const response = await fetchUserFaqs(userId, currentPage, itemsPerPage, {
                status: selectedStatus,
                category: selectedCategory,
                search: searchQuery
            });
            setFaqs(response.data.faqs);
            // Store pagination metadata from backend
            if (response.data.pagination) {
                setPaginationMeta(response.data.pagination);
            }
            // Store unread/read counts (independent of the active filter tab)
            if (response.data.status_counts) {
                setStatusCounts(response.data.status_counts);
            }
        } catch (error) {
            console.error("Error fetching Posts:", error);
        } finally {
            setLoading(false);
        }
    }, [userId, currentPage, itemsPerPage, selectedStatus, selectedCategory, searchQuery]);

    // ✅ Call fetchFaqs in useEffect
    useEffect(() => {
        fetchFaqs();
    }, [fetchFaqs]); // ✅ Fetch when filters or pagination changes

    const toggleExpand = (id, userFaqId, status) => {
        // If there's an already expanded card, check if it needs API call
        if (expandedCard !== null) {
            handleCollapse(expandedCard);
        }

        // Toggle the current card
        setExpandedCard(expandedCard === id ? null : id);
        setPreviousCard(expandedCard === id ? null : id);
    };

    // Backend now handles filtering, but we keep minimal client-side filtering for expired dates
    // (Backend filters by status, category, and search)
    const filteredMessages = faqs.filter((message) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // normalize to midnight

        const expiredTime = message.expired_date
            ? new Date(message.expired_date.replace(" ", "T"))
            : null;

        if (expiredTime) expiredTime.setHours(0, 0, 0, 0);

        const isExpired = expiredTime && expiredTime <= today;

        // Exclude expired posts (backend should handle this, but keep as safety check)
        if (isExpired) {
            return false;
        }

        // Status filters (backend handles this, but keep for UI consistency)
        if (selectedStatus !== "EXPIRED" && isExpired) {
            return false;
        }

        if (selectedStatus === "EXPIRED") {
            return isExpired;
        }

        if (selectedStatus === "unread") {
            return message.user_faq_status === "unread" || message.persistInNew;
        }

        if (selectedStatus === "read") {
            return message.user_faq_status === "read" || message.tempAccepted;
        }

        if (selectedStatus === "accepted") {
            const isAccepted = message.user_faq_status === "accepted";
            const matchesCategory =
                selectedCategory === null ||
                message.category === selectedCategory;
            return isAccepted && matchesCategory;
        }

        if (selectedStatus === "STARRED") {
            return isMessageStarred(message);
        }

        return true;
    });

    const handleCollapse = (id) => {
        const faq = faqs.find((item) => item.id === id);
        if (faq && faq.user_faq_status === "unread") {
            markFaqAsRead(faq.user_faq_id)
                .then((response) => {
                    console.log(response.data.message);

                    // Reading-speed points and any module this read completed.
                    // Completing a module takes priority - it is the bigger moment.
                    const completed = response.data?.completed_modules ?? [];
                    const points = response.data?.points_awarded;

                    if (completed.length > 0) {
                        setReadFeedback({
                            severity: "success",
                            message: `${completed[0]} complete. Well done!`,
                        });
                    } else if (points) {
                        setReadFeedback({
                            severity: "success",
                            message:
                                points >= 3
                                    ? `+${points} points — read within the hour`
                                    : `+${points} ${points === 1 ? "point" : "points"}`,
                        });
                    }

                    // ✅ Update UI state locally so the NEW/READ red dot
                    // and filters reflect the change immediately, without
                    // waiting for a refetch.
                    setFaqs((prevFaqs) =>
                        prevFaqs.map((item) =>
                            item.id === id
                                ? {
                                      ...item,
                                      user_faq_status: "read",
                                      // Keep it visible in the current NEW
                                      // view until the filter/page changes,
                                      // same pattern as handleAccept.
                                      persistInNew: selectedStatus === "unread",
                                  }
                                : item
                        )
                    );

                    // ✅ Keep the NEW/READ red dot in sync immediately
                    setStatusCounts((prev) => ({
                        ...prev,
                        unread: Math.max(0, (prev.unread ?? 0) - 1),
                        read: (prev.read ?? 0) + 1,
                    }));
                })
                .catch((error) => {
                    console.error("Error updating status:", error);
                });
        }
    };

    const handleAccept = (userFaqId) => {
        // Remember what it was before we flip it, so the NEW/READ counts
        // can be decremented from the right bucket below.
        const previousStatus = faqs.find(
            (faq) => faq.user_faq_id === userFaqId
        )?.user_faq_status;

        // ✅ Disable the button immediately for UX
        setFaqs((prevFaqs) =>
            prevFaqs.map((faq) =>
                faq.user_faq_id === userFaqId
                    ? { ...faq, tempAccepted: true }
                    : faq
            )
        );

        acceptFaq(userFaqId)
            .then((response) => {
                // console.log(response.data.message);

                setFaqs((prevFaqs) =>
                    prevFaqs.map((faq) =>
                        faq.user_faq_id === userFaqId
                            ? {
                                  ...faq,
                                  user_faq_status: "accepted",
                                  persistInNew: selectedStatus === "unread",
                              }
                            : faq
                    )
                );

                // ✅ Keep the NEW/READ red dot in sync immediately
                if (previousStatus === "unread" || previousStatus === "read") {
                    setStatusCounts((prev) => ({
                        ...prev,
                        [previousStatus]: Math.max(
                            0,
                            (prev[previousStatus] ?? 0) - 1
                        ),
                    }));
                }
            })
            .catch((error) => {
                console.error("Error updating status:", error);

                // ❌ Revert UI change if API fails
                setFaqs((prevFaqs) =>
                    prevFaqs.map((faq) =>
                        faq.user_faq_id === userFaqId
                            ? { ...faq, tempAccepted: false }
                            : faq
                    )
                );
            });
    };

    const toggleFavourite = async (faqId, userId, is_starred) => {
        try {
            // ✅ Optimistically update UI
            setFaqs((prevFaqs) =>
                prevFaqs.map((msg) =>
                    msg.id === faqId
                        ? { ...msg, tempStarred: !is_starred }
                        : msg
                )
            );

            // ✅ Call centralized API function
            await updateFavourite(faqId, userId, !is_starred);
            fetchFaqs();
        } catch (error) {
            console.error("Error updating favourite:", error);

            // ❌ Revert UI change
            setFaqs((prevFaqs) =>
                prevFaqs.map((msg) =>
                    msg.id === faqId ? { ...msg, tempStarred: is_starred } : msg
                )
            );
        }
    };

    const categoryLabels = {
        "Generell Info": "[GENERELT]",
        "Rutiner & Policy": "[RUTINER]",
        "Kasse & Betaling": "[KASSE]",
        "Salg & Promotering": "[SALG]",
        Produkter: "[PRODUKTER]",
        Tjenester: "[TJENESTER]",
        "Personal/Arbeid": "[ARBEID]",
        Kunder: "[KUNDER]",
    };
    // ✅ Drive the NEW/READ red dot from the backend-provided counts, not the
    // currently loaded/filtered `faqs` page — those only ever contain items
    // matching whatever status tab is active, so computing "any unread?"
    // from them was blind to unread posts while viewing READ/OK/etc.
    const hasUnread = statusCounts.unread > 0;
    const hasRead = statusCounts.read > 0;

    function convertEmailTextToHTML(text) {
        // Escape HTML special characters
        const escapeHTML = (str) =>
            str
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");

        // Convert URLs to links
        const linkify = (str) =>
            str.replace(
                /(https?:\/\/[^\s]+)/g,
                (url) =>
                    `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
            );

        // Escape and preserve spacing
        let escaped = escapeHTML(text);

        // Convert 2+ consecutive newlines into paragraphs
        escaped = escaped
            .split(/\n{2,}/)
            .map(
                (para) =>
                    `<p style="margin: 0 0 12px 0;">${para
                        .replace(/\n/g, "<br>")
                        .replace(/  /g, "&nbsp;&nbsp;")}</p>`
            )
            .join("");

        return linkify(escaped);
    }

    function tryToFormatUnformattedEmail(text) {
        if (!text.includes("\n")) {
            return text
                .replace(/(Hi,|hei,|Hei,)/, "$1\n\n") // Add line break after greetings
                .replace(
                    /(Thanks\.|Takk for hjelpen\.|Vennlig hilsen \/ Kind regards,\.)/,
                    "\n\n$1\n"
                ); // Add line break before and after phrases
        }
        return text;
    }

    return (
        <Box
            sx={{
                minHeight: "100vh", // Ensures full height
                display: "flex",
                flexDirection: "column", // Stack elements vertically
                alignItems: "center",
                p: { xs: 1, sm: 2 }, // Adjusts padding for smaller screens
            }}
        >
            <Box
                sx={{
                    width: { xs: "100%", sm: "90%", md: "95%" },
                    display: "flex",
                    justifyContent: "flex-start",
                    gap: 1,
                    mb: 1,
                    flexWrap: "wrap",
                    flexDirection: "column",
                }}
            >
                {/* Filters and Search Bar Row */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "row", sm: "row" },
                        gap: { xs: 1, sm: 2 },
                        alignItems: { xs: "stretch", sm: "center" },
                        mb: 2,
                        flexWrap: { xs: "nowrap", sm: "wrap" },
                    }}
                >
                    {/* Mobile: Status dropdown (replaces chips) */}
                    <FormControl
                        size="small"
                        sx={{
                            display: { xs: "block", sm: "none" },
                            minWidth: 120,
                            flex: "0 0 auto",
                        }}
                    >
                        <Select
                            value={selectedStatus ?? ""}
                            displayEmpty
                            onChange={(e) => {
                                const next = e.target.value || null;
                                setSelectedCategory(null);
                                setSelectedStatus(next);
                            }}
                            sx={{
                                borderRadius: "16px",
                                height: "32px",
                                "& .MuiOutlinedInput-input": {
                                    padding: "6.5px 14px",
                                },
                            }}
                            renderValue={(value) => {
                                if (!value) return "Filter";
                                if (value === "unread") return "NEW";
                                if (value === "read") return "READ";
                                if (value === "accepted") return "OK";
                                return String(value);
                            }}
                        >
                            <MenuItem value="">ALL</MenuItem>
                            <MenuItem value="unread">NEW</MenuItem>
                            <MenuItem value="read">READ</MenuItem>
                            <MenuItem value="accepted">OK</MenuItem>
                            <MenuItem value="STARRED">STARRED</MenuItem>
                            <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Filter Chips */}
                    <Box
                        sx={{
                            display: { xs: "none", sm: "flex" },
                            gap: 0.5, // Reduced gap from 1 to 0.5
                            flexWrap: "wrap",
                            flex: { xs: "1 1 100%", sm: "1 1 auto" },
                        }}
                    >
                        {[
                            {
                                label: "NEW",
                                value: "unread",
                                showBadge: hasUnread,
                            },
                            {
                                label: "READ",
                                value: "read",
                                showBadge: hasRead,
                            },
                            {
                                label: "OK", // This is the "OK" button
                                value: "accepted", // or "all" depending on how you want it
                                showBadge: false,
                            },
                            {
                                label: <StarIcon />,
                                value: "STARRED",
                                showBadge: false,
                            },
                            {
                                label: "EXPIRED",
                                value: "EXPIRED",
                                showBadge: false,
                            },
                        ].map((status) => (
                            <Badge
                                key={status.value}
                                variant="dot"
                                color="error"
                                invisible={!status.showBadge}
                            >
                                <Chip
                                    label={status.label}
                                    clickable
                                    onClick={() => {
                                        if (status.value === "accepted") {
                                            setSelectedCategory(null);
                                            handleStatusFilter(status.value);
                                        } else {
                                            handleStatusFilter(status.value);
                                        }
                                    }}
                                    sx={{
                                        textTransform: "uppercase",
                                        fontSize: { xs: 14, sm: 14, md: 15 },
                                        fontWeight:
                                            selectedStatus === status.value
                                                ? "bold"
                                                : "normal",
                                        background:
                                            selectedStatus === status.value
                                                ? "linear-gradient(to right, #3b82f6, #2563eb)"
                                                : "transparent",
                                        color:
                                            selectedStatus === status.value
                                                ? "white"
                                                : "inherit",
                                        border:
                                            selectedStatus === status.value
                                                ? "none"
                                                : "1px solid rgba(0,0,0,0.48)",
                                        padding: "2px 8px", // Reduced padding
                                        height: "28px", // Reduced height
                                        "& .MuiChip-label": {
                                            padding: "0 4px", // Reduced label padding
                                        },
                                        "&:hover": {
                                            background:
                                                selectedStatus === status.value
                                                    ? "linear-gradient(to right, #2563eb, #1e40af)"
                                                    : "rgba(0,0,0,0.04)",
                                        },
                                    }}
                                />
                            </Badge>
                        ))}
                    </Box>

                    {/* Search Bar - Chip Style */}
                    <TextField
                        placeholder="Search posts by title or content..."
                        variant="outlined"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            flex: { xs: "1 1 auto", sm: "0 1 300px", md: "0 1 350px" },
                            minWidth: { xs: 0, sm: "250px" },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "16px", // Match chip border radius
                                backgroundColor: "transparent",
                                height: "32px", // Match chip height
                                "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgba(0,0,0,0.48)", // Match chip border
                                    },
                                },
                                "&.Mui-focused": {
                                    backgroundColor: "transparent",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "rgba(0,0,0,0.48)", // Match chip border
                                        borderWidth: "1px", // Match chip border width
                                    },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(0,0,0,0.48)", // Match chip border color
                                    borderWidth: "1px", // Match chip border width
                                },
                            },
                            "& .MuiInputBase-input": {
                                padding: "4px 12px",
                                fontSize: { xs: 14, sm: 14, md: 15 }, // Match chip font size
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon 
                                        sx={{ 
                                            color: "rgba(0,0,0,0.54)",
                                            fontSize: "18px",
                                        }} 
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchQuery("")}
                                        sx={{ 
                                            p: 0.5,
                                            color: "rgba(0,0,0,0.54)",
                                            "&:hover": {
                                                backgroundColor: "rgba(0,0,0,0.04)",
                                            },
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        // alignItems: "center",
                        //flexWrap: "wrap", // Ensures items wrap if space is limited
                        gap: 2, // Space between elements
                    }}
                >
                    {/* CATEGORY Title */}
                    <Typography
                        sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "h6", sm: "h6", md: "h4" },
                            mt: 1,
                        }}
                    >
                        CATEGORY:
                    </Typography>

                    {selectedStatus === "accepted" && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                // gap: 1,
                            }}
                        >
                            {/* Dropdown for Mobile & iPad */}
                            <Box
                                sx={{
                                    display: {
                                        xs: "block",
                                        md: "none",
                                        sm: "none",
                                    },
                                    mt: -1.5,
                                }}
                            >
                                <FormControl
                                    variant="standard"
                                    sx={{  minWidth: 120 }}
                                >
                                    <InputLabel id="demo-simple-select-standard-label">
                                        Category
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-standard-label"
                                        id="demo-simple-select-standard"
                                        value={selectedCategory}
                                        onChange={(e) =>
                                            handleCategoryFilter(e.target.value)
                                        }
                                        label="Category"
                                        sx={{
                                            textTransform: "uppercase",
                                            // fontSize: 10,
                                        }}
                                    >
                                        <MenuItem value="">
                                            All Categories
                                        </MenuItem>
                                        {Object.keys(categoryLabels).map(
                                            (category) => (
                                                <MenuItem
                                                    key={category}
                                                    value={category}
                                                >
                                                    {categoryLabels[category]}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Desktop & Tablet Text Links */}
                            <Box
                                sx={{
                                    display: { xs: "none", sm: "flex" }, // Show on iPad (sm) and larger screens
                                    flexWrap: "wrap",
                                    gap: 1, // Reduced gap from 2 to 1
                                   // mt: 1, // Reduced margin-top from 1.5 to 1
                                }}
                            >
                                {Object.keys(categoryLabels).map((category) => (
                                    <Typography
                                        key={category}
                                        onClick={() =>
                                            handleCategoryFilter(category)
                                        }
                                        sx={{
                                            mt: 1, // Reduced margin-top from 2 to 1
                                            fontSize: 15,
                                            cursor: "pointer",
                                            fontWeight:
                                                selectedCategory === category
                                                    ? "bold"
                                                    : "normal",
                                            color:
                                                selectedCategory === category
                                                    ? getCategoryColor(category).color
                                                    : "black",
                                            backgroundColor:
                                                selectedCategory === category
                                                    ? getCategoryColor(category).bg
                                                    : "transparent",
                                            px: 1,
                                            borderRadius: "4px",
                                            "&:hover": {
                                                textDecoration: "underline",
                                                backgroundColor: getCategoryColor(category).bg,
                                            },
                                        }}
                                    >
                                        {categoryLabels[category]}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
            {/* Cards Section */}
            <Box
                sx={{
                    mt: 2,
                    width: { xs: "100%", sm: "95%", md: "95%", lg: "98%" },
                }}
            >
                {/* Search Results Indicator */}
                {searchQuery.trim() !== "" && !loading && (
                    <Box
                        sx={{
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <SearchIcon fontSize="small" />
                            {filteredMessages.length === 0
                                ? "No results found"
                                : `Found ${filteredMessages.length} result${
                                      filteredMessages.length !== 1 ? "s" : ""
                                  } for "${searchQuery}"`}
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setSearchQuery("")}
                            sx={{
                                textTransform: "none",
                                minWidth: "auto",
                                px: 1,
                            }}
                        >
                            Clear
                        </Button>
                    </Box>
                )}

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr" },
                        gap: 2,
                        alignItems: "start",
                    }}
                >
                    {loading && userId ? (
                        // Skeleton Loading UI
                        Array.from({ length: 6 }).map((_, index) => (
                            <Card
                                key={`skeleton-${index}`}
                                sx={{
                                    p: 1,
                                    bgcolor: "#f5f5f5",
                                    boxShadow: 3,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: {
                                            xs: "flex",
                                            md: "space-between",
                                        },
                                        flexDirection: {
                                            xs: "column",
                                            md: "row",
                                        },
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {/* Title Skeleton */}
                                    <Skeleton
                                        variant="text"
                                        width="60%"
                                        height={40}
                                        sx={{ ml: 2, mb: 1 }}
                                    />
                                    {/* Action Buttons Skeleton */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "8px",
                                        }}
                                    >
                                        <Skeleton
                                            variant="circular"
                                            width={40}
                                            height={40}
                                        />
                                        <Skeleton
                                            variant="circular"
                                            width={40}
                                            height={40}
                                        />
                                        <Box
                                            sx={{
                                                display: { xs: "none", md: "flex" },
                                                gap: 1,
                                            }}
                                        >
                                            <Skeleton
                                                variant="circular"
                                                width={25}
                                                height={25}
                                                sx={{ mt: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="circular"
                                                width={25}
                                                height={25}
                                                sx={{ mt: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="circular"
                                                width={25}
                                                height={25}
                                                sx={{ mt: 0.5 }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                {/* Content Skeleton */}
                                <Box sx={{ ml: 2, mr: 1.5, mt: 2 }}>
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={120}
                                        sx={{
                                            borderRadius: 1,
                                            mb: 2,
                                        }}
                                    />
                                    {/* Button and metadata skeleton */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 2,
                                            mt: 1,
                                        }}
                                    >
                                        <Skeleton
                                            variant="rectangular"
                                            width={100}
                                            height={36}
                                        />
                                        <Skeleton variant="text" width={120} />
                                        <Skeleton variant="text" width={100} />
                                    </Box>
                                </Box>
                            </Card>
                        ))
                    ) : (
                        filteredMessages.map((message) => (
                            <Card
                                key={message.id}
                                sx={{
                                    p: 0.5,
                                    bgcolor: "#f5f5f5",
                                    boxShadow: 3,
                                    transition: "0.3s",
                                    "&:hover": { boxShadow: 6 },
                                    height: "fit-content", // Allow cards to have different heights
                                    gridColumn: "span 1",
                                      
                                }}
                            >
                                {/* Header Section */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: {
                                            xs: "flex",
                                            md: "space-between",
                                        },
                                        flexDirection: {
                                            xs: "column",
                                            md: "row",
                                        },
                                        cursor: "pointer",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                                        <Tooltip title={message.category || "No Category"}>
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: "14px",
                                                    fontWeight: "bold",
                                                    bgcolor: getCategoryColor(message.category).bg,
                                                    color: getCategoryColor(message.category).color,
                                                    ml: 1,
                                                }}
                                            >
                                                {(message.category || "NC").substring(0, 2).toUpperCase()}
                                            </Avatar>
                                        </Tooltip>
                                        <Typography
                                            fontWeight={"bold"}
                                           // variant={{ xs: "h5", sm: "h5" }}
                                            onClick={() => toggleExpand(message.id)}
                                            sx={{
                                                ml: 1,
                                                mr: 1.5,
                                                mb: 1,
                                                textAlign: "left",
                                                wordBreak: "break-word",
                                                fontSize: { xs: "14px", sm: "16px" },
                                            }}
                                        >
                                            {message.title}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "8px",
                                            alignItems: "center",
                                            mb: 1,
                                        }}
                                    >
                                        <Tooltip title="Expand/Collapse Post">
                                            <IconButton
                                                data-tour="expand-post"
                                                size="small"
                                                color="primary"
                                                onClick={() => toggleExpand(message.id)}
                                            >
                                                {expandedCard === message.id ? (
                                                    <KeyboardArrowUpIcon />
                                                ) : (
                                                    <KeyboardArrowDownIcon />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Mark Favourite Post">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() =>
                                                    toggleFavourite(
                                                        message.id,
                                                        userId,
                                                        isMessageStarred(
                                                            message
                                                        )
                                                    )
                                                }
                                            >
                                                {isMessageStarred(message) ? (
                                                    <StarIcon />
                                                ) : (
                                                    <StarBorderIcon />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                        {/* Category name for mobile view */}
                                        <Typography
                                            sx={{
                                                display: { xs: "block", md: "none" },
                                                fontSize: 12,
                                                fontWeight: "bold",
                                                color: getCategoryColor(message.category).color,
                                                backgroundColor: getCategoryColor(message.category).bg,
                                                px: 0.75,
                                                py: 0.25,
                                                borderRadius: "4px",
                                                ml: 0.5,
                                            }}
                                        >
                                            {message.category}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: { xs: "none", md: "flex" },
                                                gap: 0.5,
                                            }}
                                        >
                                            <Tooltip title="Unread Post">
                                                <Avatar
                                                    sx={{
                                                        bgcolor:
                                                            message.user_faq_status ===
                                                            "unread"
                                                                ? red[500]
                                                                : "#e0e0e0",
                                                                width: { xs: 30, sm: 25 },
                                                                height: { xs: 30, sm: 25 },
                                                                fontSize: { xs: 14, sm: 12 },
                                                    }}
                                                />
                                                    
                                            </Tooltip>
                                            <Tooltip title="Read Post">
                                                <Avatar
                                                    sx={{
                                                        bgcolor:
                                                            message.user_faq_status ===
                                                            "read"
                                                                ? yellow[500]
                                                                : "#e0e0e0",
                                                                width: { xs: 30, sm: 25 },
                                                                height: { xs: 30, sm: 25 },
                                                                fontSize: { xs: 14, sm: 12 },
                                                    }}
                                                />
                                                    
                                            </Tooltip>
                                            <Tooltip title="Accepted Post">
                                                <Avatar
                                                    sx={{
                                                        bgcolor:
                                                            message.user_faq_status ===
                                                            "accepted"
                                                                ? green[500]
                                                                : "#e0e0e0",
                                                        width: { xs: 30, sm: 25 },
                                                        height: { xs: 30, sm: 25 },
                                                        fontSize: { xs: 14, sm: 12 },
                                                    }}
                                                />
                                                   
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Collapsible Content */}
                                <Collapse
                                    in={expandedCard === message.id}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                        }}
                                    >
                                        <Box
                                            data-tour="read-post"
                                            className="email-preview-content"
                                            sx={{
                                                whiteSpace: "normal",
                                                textAlign: "left",
                                                fontFamily: "monospace",
                                                fontSize: "14px",
                                                backgroundColor: "#f9f9f9",
                                                p: 2,
                                                mt: 1,
                                                border: "1px solid #ddd",
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: message.content.includes("<")
                                                    ? message.content
                                                    : convertEmailTextToHTML(
                                                          tryToFormatUnformattedEmail(
                                                              message.content
                                                          )
                                                      ),
                                            }}
                                        />
                                        
                                        {message.attachments &&
                                            message.attachments.length > 0 && (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 1,
                                                        alignItems: "flex-start",
                                                        ml: 2,
                                                    }}
                                                >
                                                    {message.attachments.map((file) => {
                                                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.file_name);
                                                        const isPDF = /\.pdf$/i.test(file.file_name);
                                                        return (
                                                            <Box key={file.id}>
                                                                {isImage && file.is_inline == "0" && (
                                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                                        <img
                                                                            src={file.url}
                                                                            alt={file.file_name}
                                                                            style={{
                                                                                maxWidth: "50px",
                                                                                maxHeight: "50px",
                                                                                borderRadius: "8px",
                                                                                objectFit: "cover",
                                                                                cursor: "pointer",
                                                                            }}
                                                                            onClick={() => handleImageClick(file.url)}
                                                                        />
                                                                        <Link href={file.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                                                            {file.file_name}
                                                                        </Link>
                                                                    </Box>
                                                                )}
                                                                {isPDF && file.is_inline == "0" && (
                                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                                        <PictureAsPdfIcon color="error" />
                                                                        <Link href={file.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                                                            {file.file_name}
                                                                        </Link>
                                                                    </Box>
                                                                )}
                                                                {!isImage && !isPDF && file.is_inline == "0" && (
                                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                                        <InsertDriveFileIcon color="action" />
                                                                        <Link href={file.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                                                            {file.file_name}
                                                                        </Link>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}

                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                flexDirection: { xs: "column", lg: "row" },
                                                gap: 1,
                                                mt: 1,
                                                ml: 2,
                                                pb: 2
                                            }}
                                        >
                                            <Button
                                                data-tour="read-ok-button"
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={() => handleAccept(message.user_faq_id)}
                                                disabled={message.tempAccepted || message.user_faq_status === "accepted"}
                                            >
                                                READ OK
                                            </Button>

                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: getCategoryColor(message.category).color,
                                                        backgroundColor: getCategoryColor(message.category).bg,
                                                        px: 1,
                                                        borderRadius: "4px",
                                                    }}
                                                >
                                                    {message.category}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {message.publisher}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Pub: {new Date(message.email_sent_time || message.updated_at).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }).replace(/\//g, ".")}
                                                </Typography>
                                                {message.expired_date && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Exp: {new Date(message.expired_date).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }).replace(/\//g, ".")}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Collapse>
                            </Card>
                        ))
                    )}
                </Box>
            </Box>

            {/* Pagination Controls */}
            {paginationMeta && paginationMeta.total > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'center', sm: 'center' },
                        gap: { xs: 2, sm: 3 },
                        mt: 3,
                        mb: 2,
                        px: { xs: 1, sm: 2 },
                    }}
                >
                    {/* Items per page selector */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                color: 'text.secondary',
                            }}
                        >
                            Show:
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 100 } }}>
                            <Select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    height: { xs: '32px', sm: '36px' },
                                }}
                            >
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                                <MenuItem value={200}>200</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                color: 'text.secondary',
                            }}
                        >
                            of {paginationMeta.total} posts
                        </Typography>
                    </Box>

                    {/* Pagination component */}
                    {paginationMeta.total_pages > 1 && (
                        <Pagination
                            count={paginationMeta.total_pages}
                            page={currentPage}
                            onChange={(event, value) => setCurrentPage(value)}
                            color="primary"
                            size="small"
                            showFirstButton
                            showLastButton
                            sx={{
                                '& .MuiPagination-ul': {
                                    justifyContent: 'center',
                                },
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Welcome Tour Modal */}
            <WelcomeTour
                open={showWelcomeTour}
                onClose={() => setShowWelcomeTour(false)}
                onComplete={async () => {
                    try {
                        await markWelcomeTourCompleted();
                        setShowWelcomeTour(false);
                    } catch (error) {
                        console.error('Error marking welcome tour as completed:', error);
                        setShowWelcomeTour(false);
                    }
                }}
            />

            {/* Points earned, or a module finished, right after reading a post */}
            <Snackbar
                open={Boolean(readFeedback)}
                autoHideDuration={3500}
                onClose={() => setReadFeedback(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={readFeedback?.severity || "success"}
                    onClose={() => setReadFeedback(null)}
                    sx={{ fontWeight: 600 }}
                >
                    {readFeedback?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Home;
